import {useButton} from 'react-aria'
import {
  ButtonContext,
  type ButtonProps,
  type PressEvent,
  type SlotProps,
  useContextProps,
} from 'react-aria-components'

import style from './PrimaryButton.module.scss'

interface Props extends ButtonProps {
  size?: 'xl' | 'lg' | 'md' | 'sm'
  isPending?: boolean
  showPendingIndicator?: boolean
  pendingIndicatorClassName?: string
  ref?: Ref<HTMLButtonElement>
}

const PrimaryButton = deepMemo<Props>(function PrimaryButton({
  ref,
  showPendingIndicator = true,
  pendingIndicatorClassName,
  onPress,
  ...props
}) {
  const handlePress = useStableCallback((e: PressEvent) => {
    if (props.isPending) return
    if (props.isDisabled) return
    onPress?.(e)
  })

  ;[props, ref] = useContextProps<MemoizedProps<ButtonProps>, SlotProps, HTMLButtonElement>(
    {
      ...props,
      onPress: handlePress,
      isDisabled: props.isDisabled ?? props.isPending,
    },
    ref,
    ButtonContext,
  )

  const {buttonProps} = useButton(props, ref)

  const defaultClassName = clsx(
    style.Button,
    style.Rounded,
    (() => {
      switch (props.size) {
        case 'xl':
          return style.ExtraLarge
        case 'lg':
          return style.Large
        case 'md':
          return style.Medium
        case 'sm':
          return style.Small
        case undefined:
          return style.Large
      }
    })(),
  )

  return (
    <button
      {...buttonProps}
      type='button'
      ref={ref}
      className={clsx(defaultClassName, props.className as string)}
      disabled={false}
      data-pending={props.isPending}
    >
      {showPendingIndicator && props.isPending && (
        <div
          className={clsx(
            style.Loader,
            props.size === 'sm' && style.Small,
            pendingIndicatorClassName,
          )}
        />
      )}
      {props.children as ReactNode}
    </button>
  )
})()

export default PrimaryButton
